import { memo, useCallback } from 'react';
import { MessageSquare, Pencil } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Card, Tooltip } from '@aimsos/ui';

/**
 * Agent tile for the list grid — Card + Badge + Button (@aimsos/ui).
 * Mirrors WorkspaceGridCard structure and memo usage.
 */
const AgentGridCard = memo(({ agent, onEdit, onChat }) => {
  const { t } = useTranslation('agents');

  const handleEdit = useCallback(
    (e) => { e.stopPropagation(); onEdit?.(agent); },
    [onEdit, agent],
  );

  const handleChat = useCallback(
    (e) => { e.stopPropagation(); onChat?.(agent); },
    [onChat, agent],
  );

  if (!agent) return null;

  const label = agent.category || agent.type;

  return (
    <Card className="flex flex-col p-5 shadow-sm transition-shadow hover:shadow-md">
      {/* Title + category pinned to the top — no flex-1 on h3 so Service never shifts down */}
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="min-w-0 text-base font-semibold leading-snug text-content line-clamp-1">
          <Tooltip content={agent.name} position="top" align="start" delay={400}>
            {agent.name}
          </Tooltip>
        </h3>
        <span className="text-xs text-content-muted">{label ?? '\u00a0'}</span>
      </div>

      {/* Description grows to fill remaining card height, keeping footer at the bottom */}
      <div className="mt-2 flex-1">
        {agent.description ? (
          <p className="text-sm text-content-muted line-clamp-2">{agent.description}</p>
        ) : null}
      </div>

      <Card.Footer className="mt-4 !border-t !border-edge !px-0 !py-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={Pencil}
            onClick={handleEdit}
          >
            {t('list.card.edit')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            leftIcon={MessageSquare}
            onClick={handleChat}
          >
            {t('list.card.chat')}
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
});

AgentGridCard.displayName = 'AgentGridCard';

export default AgentGridCard;
